import { clx } from "@medusajs/ui"
import Image from "next/image"
import React from "react"

import PlaceholderImage from "@/modules/common/icons/placeholder-image"

type ThumbnailProps = {
  thumbnail?: string | null
  // TODO: Fix image typings
  images?: any[] | null
  size?: "small" | "medium" | "large" | "full" | "square"
  isFeatured?: boolean
  className?: string
  type?: "preview" | "full"
  "data-testid"?: string
  priority?: boolean
  alt?: string
}

const Thumbnail: React.FC<ThumbnailProps> = ({
  thumbnail,
  images,
  size = "small",
  isFeatured,
  className,
  "data-testid": dataTestid,
  type,
  priority,
  alt,
}) => {
  const initialImage = thumbnail || images?.[0]?.url

  return (
    <div
      className={clx("relative w-full overflow-hidden", className, {
        "aspect-[11/14]": isFeatured,
        "aspect-[9/16]": !isFeatured && size !== "square",
        "aspect-[1/1]": size === "square",
        "w-[180px]": size === "small",
        "w-[290px]": size === "medium",
        "w-[440px]": size === "large",
        "w-full": size === "full",
      })}
      data-testid={dataTestid}
    >
      <ImageOrPlaceholder image={initialImage} size={size} type={type} priority={priority} alt={alt} />
    </div>
  )
}

const ImageOrPlaceholder = ({
  image,
  size,
  type,
  priority,
  alt,
}: Pick<ThumbnailProps, "size" | "type" | "priority" | "alt"> & {
  image?: string
}) => {
  return image ? (
    <Image
      src={image}
      alt={alt ?? ""}
      className={clx("absolute inset-0 w-full h-full object-contain", {
        "p-4": type === "full",
        "p-2": type === "preview",
      })}
      draggable={false}
      quality={50}
      sizes="(max-width: 512px) 70vw, (max-width: 1024px) 33vw, 300px"
      // explicit intrinsic size (CLS); the <img> is absolute w-full h-full inside an aspect-ratio box
      width={400}
      height={400}
      priority={priority}
      // Next 15's priority preloads but doesn't set fetchpriority; the first grid cards are the mobile LCP (1 Oct 2026)
      fetchPriority={priority ? "high" : undefined}
    />
  ) : (
    <div className="w-full h-full absolute inset-0 flex items-center justify-center">
      <PlaceholderImage size={size === "small" ? 16 : 24} />
    </div>
  )
}

export default Thumbnail
